import React, { useCallback, useState, MouseEvent, useMemo } from 'react';
import { isNull, noop } from 'lodash/fp';

import { AnalysisView } from '../../documents/store/types';
import { AnalysisViewButtons } from './AnalysisViewButtons';
import { IconButton } from '../button/IconButton';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getUserHasFeaturePermission } from '../../auth/login/store';
import styles from './Analysis.module.scss';
import { Add, Robot, Calculator, Search, User, SmileyNeutral, Engine } from '../icons';
import { Position } from '../modal/PositionModal';
import { Action, ActionModal } from '../modal/ActionModal';
import { EntityModalType, getModalOpen as getEntityModalOpen, searchEntitiesStarted, toggleModal as toggleClientEntityModal, upsertEntityStarted } from '../../admin/entity/store';
import { EntityModal as CreateEntityModal } from '../../admin/entity/EntityModal';
import { SearchEntityModal } from '../../admin/entity/SearchEntityModal';
import { FeaturePermission } from '../../admin/users/store';
import { toggleCalculatorHelpModal, toggleRiskToleranceHelpModal } from '../../admin/dataset-builder/store';
import { CalculatorHelpModal } from '../calculator/CalculatorHelpModal';
import { getRiskToleranceForDefinitions } from '../../datasets/instances/store';
import { RiskToleranceHelpModal } from '../risk-tolerance/RiskToleranceHelpModal';
import { CustomDocumentQuery } from '../ai-question/CustomDocumentQuery';
import { toggleChatbotModal } from '../../documents/chatbot/store';
import { Chatbot } from '../icons/Chatbot';

const { primary, white, grey } = styles;

interface AnalysisSideMenuProps {
    toggleAnalysisView: (view: AnalysisView) => void;
    currentView: AnalysisView;
    isPermittedToChangeView?: boolean;
    toggleMLModal?: () => void;
    showMLDataOption?: boolean;
    mlDataModalDisabled?: boolean;
    isAIPlayground?: boolean;
    showMLLatestIndicator?: boolean;
    showChatbotIcon?: boolean;
    showNettingAnalysisOption?: boolean;
    toggleNettingAnalysis?: () => void;
    nettingAnalysisColour?: string;
}

const ICON_FONT_SIZE = 30;

export const AnalysisSideMenu: React.FC<AnalysisSideMenuProps> = ({
    toggleAnalysisView,
    currentView,
    isPermittedToChangeView = true,
    toggleMLModal = noop,
    showMLDataOption,
    mlDataModalDisabled = false,
    isAIPlayground = false,
    showMLLatestIndicator = false,
    showChatbotIcon = false,
    showNettingAnalysisOption = false,
    toggleNettingAnalysis = noop,
    nettingAnalysisColour = grey
}) => {
    const [entityModalPosition, setEntityModalPosition] = useState<Position | null>(null);

    const dispatch = useAppDispatch();
    const entityModalOpen = useAppSelector(getEntityModalOpen);
    const hasEntityManagementPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.ENTITY_MANAGEMENT]));

    const riskTolerance = useAppSelector(getRiskToleranceForDefinitions);
    const showRiskFieldIndicator = riskTolerance.length > 0;

    const openEntityActionModal = useCallback((e: MouseEvent<HTMLButtonElement>) => setEntityModalPosition({ x: e.clientX, y: e.clientY }), []);
    const closeEntityActionModal = () => setEntityModalPosition(null);

    const toggleEntityModal = useCallback((value: EntityModalType | null) => dispatch(toggleClientEntityModal(value)), [dispatch]);
    const openCreateEntityModal = useCallback(() => { toggleEntityModal(EntityModalType.CREATE); }, [toggleEntityModal]);
    const openSearchEntityModal = useCallback(() => { toggleEntityModal(EntityModalType.SEARCH); }, [toggleEntityModal]);
    const closeEntityModal = useCallback(() => toggleEntityModal(null), [toggleEntityModal]);

    const uploadEntity = useCallback(() => dispatch(upsertEntityStarted()), [dispatch]);
    const searchEntities = useCallback(() => dispatch(searchEntitiesStarted()), [dispatch]);

    const toggleCalculatorHelp = useCallback(() => dispatch(toggleCalculatorHelpModal()), [dispatch]);

    const toggleRiskToleranceHelp = useCallback(() => dispatch(toggleRiskToleranceHelpModal()), [dispatch]);

    const openChatbotModal = useCallback(() => dispatch(toggleChatbotModal(true)), [dispatch]);

    const entityActions: Action[] = useMemo(() => [
        { label: 'Add Entity', icon: Add, onClick: openCreateEntityModal },
        { label: 'LEI Search', icon: Search, onClick: openSearchEntityModal }
    ], [openCreateEntityModal, openSearchEntityModal]);

    const newIndicator = useMemo(() => <div className={styles.newIndicator}>NEW</div>, []);

    return (
        <>
            <div className={styles.sideMenuWrapper}>
                <AnalysisViewButtons toggleAnalysisView={toggleAnalysisView} currentView={currentView} disabled={!isPermittedToChangeView} />
                {(hasEntityManagementPermission && !isAIPlayground) &&
                    <div className={styles.sectionWrapper}>
                        <div className={styles.divider} />
                        <IconButton icon={User} onClick={openEntityActionModal} fontSize={ICON_FONT_SIZE} />
                    </div>
                }
                {showMLDataOption &&
                    <div className={styles.sectionWrapper}>
                        <div className={styles.divider} />
                        <IconButton icon={Robot} onClick={toggleMLModal} fontSize={ICON_FONT_SIZE} overlayText='View AI Data' disabled={mlDataModalDisabled} showIndicator={!mlDataModalDisabled && showMLLatestIndicator} indicator={newIndicator} />
                    </div>
                }
                {showChatbotIcon &&
                    <div className={styles.sectionWrapper}>
                        {!showMLDataOption && <div className={styles.divider} />}
                        <IconButton icon={Chatbot} onClick={openChatbotModal} fontSize={ICON_FONT_SIZE} overlayText='Ask a question' />
                    </div>
                }
                {showNettingAnalysisOption &&
                    <div className={styles.sectionWrapper}>
                        <div className={styles.divider} />
                        <IconButton icon={Engine} color={nettingAnalysisColour} onClick={toggleNettingAnalysis} fontSize={25} overlayText='See Netting Determination' />
                    </div>
                }
                {!isAIPlayground &&
                    <div className={styles.sectionWrapper}>
                        <div className={styles.divider} />
                        <IconButton icon={Calculator} onClick={toggleCalculatorHelp} fontSize={ICON_FONT_SIZE} />
                    </div>
                }
                {(showRiskFieldIndicator && !isAIPlayground) &&
                    <div className={styles.sectionWrapper}>
                        <div className={styles.divider} />
                        <IconButton icon={SmileyNeutral} onClick={toggleRiskToleranceHelp} fontSize={ICON_FONT_SIZE} color={primary} iconFill={white} />
                    </div>
                }
            </div>
            <ActionModal
                isOpen={!isNull(entityModalPosition)}
                actions={entityActions}
                closeModal={closeEntityActionModal}
                position={entityModalPosition}
                testId='analysis-entity'
            />
            <CreateEntityModal
                uploadEntity={uploadEntity}
                closeModal={closeEntityModal}
                isOpen={entityModalOpen === EntityModalType.CREATE}
            />
            <SearchEntityModal
                searchEntities={searchEntities}
                closeModal={closeEntityModal}
                isOpen={entityModalOpen === EntityModalType.SEARCH}
            />
            <CalculatorHelpModal />
            <RiskToleranceHelpModal />
            <CustomDocumentQuery />
        </>
    );
};
